import { ReactNode, useState } from "react";
import { Eye, Download, Lock, Link, Pencil, Trash2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import FileIcon from "@/assets/icons/file.svg?react";
import {
  ExpeditionDocumentFileBoDto,
  ExpeditionDocumentFileDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useDisplayDocument } from "@/hooks/useDisplayDocument";
import { useDownloadFile } from "@/hooks/useDownloadFile";
import { EditFileModal } from "./editFileModal";
import { useFileTypeLabels } from "@/hooks/useEnumLabels";

type DocumentCardShellProps = {
  name: string;
  typeLabel: string;
  meta?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  hideDetailsInfo?: boolean;
};

export const DocumentCardShell = ({
  name,
  typeLabel,
  meta,
  badges,
  actions,
  hideDetailsInfo = false,
}: DocumentCardShellProps) => (
  <div className="flex w-full min-w-0 items-center gap-3 rounded-lg border bg-gray-50 p-3">
    <div className="shrink-0 rounded-full border p-2">
      <FileIcon className="size-4" />
    </div>

    <div className="flex min-w-0 flex-1 flex-col gap-1 overflow-hidden">
      <p className="truncate text-sm font-medium text-gray-800" title={name}>
        {name}
      </p>
      {!hideDetailsInfo && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="lightGray">{typeLabel}</Badge>
          {meta}
          {badges}
        </div>
      )}
    </div>

    {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
  </div>
);

type DeleteFileConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  fileName: string;
  onConfirm: () => Promise<void>;
};

const DeleteFileConfirmModal = ({
  open,
  onClose,
  fileName,
  onConfirm,
}: DeleteFileConfirmModalProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const [isPending, setIsPending] = useState(false);

  const handleConfirm = async () => {
    setIsPending(true);
    await onConfirm();
    setIsPending(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("documentCard.deleteTitle")}
      description={t("documentCard.deleteDescription")}
      size="sm"
      footer={
        <>
          <Button variant="outline" type="button" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
          <Button variant="destructive" type="button" onClick={handleConfirm} disabled={isPending}>
            {isPending ? <Loader2 className="animate-spin" /> : t("documentCard.deleteConfirm")}
          </Button>
        </>
      }
    >
      <div className="flex min-w-0 items-center gap-3 rounded-lg border bg-gray-50 p-3">
        <FileIcon className="size-4 shrink-0" />
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{fileName}</p>
      </div>
    </Modal>
  );
};

type ExpeditionDocumentCardProps = {
  file: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto;
  canBeView?: boolean;
  canBeDownloaded?: boolean;
  onToggleSendViaApi?: (fileId: string, value: boolean) => Promise<void>;
  onToggleIsPrivate?: (fileId: string, value: boolean) => Promise<void>;
  onView?: (path: string) => void;
  onDownload?: (file: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto) => void;
  onDelete?: (fileId: string) => Promise<void>;
  expedition?: GetOneExpeditionBoResponseDto;
  hideDetailsInfo?: boolean;
};

export const ExpeditionDocumentCard = ({
  file,
  canBeView = true,
  canBeDownloaded = true,
  onToggleSendViaApi,
  onToggleIsPrivate,
  onView: onViewOverride,
  onDownload: onDownloadOverride,
  onDelete,
  expedition,
  hideDetailsInfo = false,
}: ExpeditionDocumentCardProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const fileTypeLabels = useFileTypeLabels();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { displayDocument } = useDisplayDocument();
  const { downloadFile } = useDownloadFile();

  const handleView = onViewOverride ?? displayDocument;
  const handleDownload =
    onDownloadOverride ??
    ((documentFile: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto) =>
      downloadFile(documentFile.path, documentFile.name));

  const formattedDate = new Date(file.createdAt).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const typeLabel = fileTypeLabels[file.type as FileTypeEnum] ?? file.type;

  const showEditButton = !!onToggleSendViaApi || !!onToggleIsPrivate;

  return (
    <>
      <DocumentCardShell
        name={file.name}
        typeLabel={typeLabel}
        meta={<span className="text-xs text-gray-500">{formattedDate}</span>}
        badges={
          <>
            {"isPrivate" in file && file.isPrivate && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Lock className="size-3" /> {t("documentCard.privateBadge")}
              </Badge>
            )}
            {"sendViaApi" in file && file.sendViaApi && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Link className="size-3" /> API
              </Badge>
            )}
          </>
        }
        actions={
          <>
            {canBeView && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("documentCard.open")}
                onClick={() => handleView(file.path)}
              >
                <Eye className="size-4" />
              </Button>
            )}
            {canBeDownloaded && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("documentCard.download")}
                onClick={() => handleDownload(file)}
              >
                <Download className="size-4" />
              </Button>
            )}
            {showEditButton && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("documentCard.edit")}
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="size-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-red-500 hover:text-red-600"
                type="button"
                title={t("documentCard.delete")}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </>
        }
        hideDetailsInfo={hideDetailsInfo}
      />

      {showEditButton && (
        <EditFileModal
          open={editOpen}
          onOpenChange={setEditOpen}
          file={file}
          onToggleSendViaApi={onToggleSendViaApi}
          onToggleIsPrivate={onToggleIsPrivate}
          expedition={expedition}
        />
      )}

      {onDelete && (
        <DeleteFileConfirmModal
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          fileName={file.name}
          onConfirm={() => onDelete(file.id)}
        />
      )}
    </>
  );
};

type NewFileDocumentCardProps = {
  file: { fileId: string; file: File; type: FileTypeEnum };
  onDelete?: (fileId: string) => Promise<void>;
  hideDetailsInfo?: boolean;
};

export const NewFileDocumentCard = ({
  file,
  onDelete,
  hideDetailsInfo = false,
}: NewFileDocumentCardProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const fileTypeLabels = useFileTypeLabels();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { displayDocument } = useDisplayDocument();

  const typeLabel = fileTypeLabels[file.type] ?? file.type;

  return (
    <>
      <DocumentCardShell
        name={file.file.name}
        typeLabel={typeLabel}
        hideDetailsInfo={hideDetailsInfo}
        actions={
          <>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              type="button"
              title={t("documentCard.open")}
              onClick={() => displayDocument(file.file)}
            >
              <Eye className="size-4" />
            </Button>
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8 text-red-500 hover:text-red-600"
                type="button"
                title={t("documentCard.delete")}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </>
        }
      />

      {onDelete && (
        <DeleteFileConfirmModal
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          fileName={file.file.name}
          onConfirm={() => onDelete(file.fileId)}
        />
      )}
    </>
  );
};
